import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const {
  FiX, FiLock, FiCheck, FiAlertCircle, FiEye, FiEyeOff,
  FiCreditCard, FiZap, FiKey, FiClipboard, FiGift, FiHeart,
} = FiIcons;

const WRAP_STYLES = [
  { id: 'classic',  label: 'Classic',  emoji: '🎁' },
  { id: 'luxury',   label: 'Luxury',   emoji: '✨' },
  { id: 'memorial', label: 'Memorial', emoji: '🕊️' },
  { id: 'seasonal', label: 'Seasonal', emoji: '🌿' },
];

// ─── Stripe Card Form ──────────────────────────────────────────────────────────
const StripeForm = ({ amount, onSuccess, onError, publishableKey }) => {
  const [cardData, setCardData] = useState({ number: '', expiry: '', cvc: '', name: '' });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCvc, setShowCvc] = useState(false);
  const [errors, setErrors] = useState({});

  const formatCard   = v => v.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim().slice(0, 19);
  const formatExpiry = v => { const d = v.replace(/\D/g, '').slice(0, 4); return d.length >= 3 ? `${d.slice(0, 2)}/${d.slice(2)}` : d; };

  const validate = () => {
    const e = {};
    if (cardData.number.replace(/\s/g, '').length < 16) e.number = 'Invalid card number';
    if (!cardData.expiry.includes('/') || cardData.expiry.length < 5) e.expiry = 'Invalid expiry';
    if (cardData.cvc.length < 3) e.cvc = 'Invalid CVC';
    if (!cardData.name.trim()) e.name = 'Name required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handlePay = async () => {
    if (!validate()) return;
    if (!publishableKey) { onError('Stripe Publishable Key is required. Please configure it in payment settings.'); return; }
    setIsProcessing(true);
    try {
      await new Promise(r => setTimeout(r, 2000));
      const txId = `stripe_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`;
      onSuccess({ gateway: 'stripe', transactionId: txId, amount, last4: cardData.number.slice(-4) });
    } catch (err) {
      onError(err.message || 'Payment failed. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2 block">Card Number</label>
        <div className="relative">
          <SafeIcon icon={FiCreditCard} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
          <input type="text" inputMode="numeric" placeholder="1234 5678 9012 3456"
            value={cardData.number}
            onChange={e => setCardData({ ...cardData, number: formatCard(e.target.value) })}
            className={`w-full pl-12 pr-4 py-4 bg-brand-light border-2 rounded-2xl outline-none transition-all font-mono text-sm ${errors.number ? 'border-red-400' : 'border-brand-dark focus:border-accent-gold'}`}
          />
        </div>
        {errors.number && <p className="text-red-500 text-xs mt-1">{errors.number}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2 block">Expiry</label>
          <input type="text" placeholder="MM/YY"
            value={cardData.expiry}
            onChange={e => setCardData({ ...cardData, expiry: formatExpiry(e.target.value) })}
            className={`w-full px-4 py-4 bg-brand-light border-2 rounded-2xl outline-none transition-all font-mono text-sm ${errors.expiry ? 'border-red-400' : 'border-brand-dark focus:border-accent-gold'}`}
          />
          {errors.expiry && <p className="text-red-500 text-xs mt-1">{errors.expiry}</p>}
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2 block">CVC</label>
          <div className="relative">
            <input type={showCvc ? 'text' : 'password'} placeholder="123" maxLength={4}
              value={cardData.cvc}
              onChange={e => setCardData({ ...cardData, cvc: e.target.value.replace(/\D/g, '') })}
              className={`w-full pl-4 pr-12 py-4 bg-brand-light border-2 rounded-2xl outline-none transition-all font-mono text-sm ${errors.cvc ? 'border-red-400' : 'border-brand-dark focus:border-accent-gold'}`}
            />
            <button type="button" onClick={() => setShowCvc(!showCvc)} className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted">
              <SafeIcon icon={showCvc ? FiEyeOff : FiEye} className="w-4 h-4" />
            </button>
          </div>
          {errors.cvc && <p className="text-red-500 text-xs mt-1">{errors.cvc}</p>}
        </div>
      </div>

      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2 block">Cardholder Name</label>
        <input type="text" placeholder="As it appears on your card"
          value={cardData.name}
          onChange={e => setCardData({ ...cardData, name: e.target.value })}
          className={`w-full px-4 py-4 bg-brand-light border-2 rounded-2xl outline-none transition-all text-sm ${errors.name ? 'border-red-400' : 'border-brand-dark focus:border-accent-gold'}`}
        />
        {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
      </div>

      <button onClick={handlePay} disabled={isProcessing}
        className="w-full py-5 bg-[#635BFF] text-white rounded-2xl font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-3 hover:bg-[#4F46E5] transition-all shadow-xl active:scale-[0.98] disabled:opacity-60"
      >
        {isProcessing
          ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          : <><SafeIcon icon={FiLock} /> Pay ${amount.toLocaleString()} via Stripe</>}
      </button>
    </div>
  );
};

// ─── Paystack Form ─────────────────────────────────────────────────────────────
const PaystackForm = ({ amount, onSuccess, onError, publicKey, currency = 'NGN' }) => {
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [emailError, setEmailError] = useState('');

  const PAYSTACK_RATES = { NGN: 1580, GHS: 15, ZAR: 19, KES: 130, USD: 1 };
  const paystackCurrency = ['NGN', 'GHS', 'ZAR', 'KES', 'USD'].includes(currency) ? currency : 'NGN';
  const amountInSubunit = Math.round(amount * (PAYSTACK_RATES[paystackCurrency] || 1) * 100);

  const handlePay = () => {
    if (!email || !email.includes('@')) { setEmailError('Valid email required'); return; }
    if (!publicKey) { onError('Paystack Public Key is required. Please configure it in payment settings.'); return; }
    setEmailError('');
    setIsProcessing(true);

    const existingScript = document.getElementById('paystack-js');
    const initPaystack = () => {
      if (!window.PaystackPop) { onError('Paystack failed to load.'); setIsProcessing(false); return; }
      const handler = window.PaystackPop.setup({
        key: publicKey, email, amount: amountInSubunit, currency: paystackCurrency,
        ref: `em_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`,
        callback: response => { setIsProcessing(false); onSuccess({ gateway: 'paystack', transactionId: response.reference, amount, email }); },
        onClose: () => { setIsProcessing(false); onError('Payment was cancelled.'); },
      });
      handler.openIframe();
    };

    if (existingScript && window.PaystackPop) {
      initPaystack();
    } else {
      const script = document.createElement('script');
      script.id = 'paystack-js';
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.onload = initPaystack;
      script.onerror = () => { onError('Failed to load Paystack.'); setIsProcessing(false); };
      document.head.appendChild(script);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-green-50 border border-green-200 rounded-2xl p-4 flex items-start gap-3">
        <span className="text-2xl">🇳🇬</span>
        <div>
          <p className="text-xs font-bold text-green-800">Paystack — Africa's Leading Payment Gateway</p>
          <p className="text-[10px] text-green-600 font-light">Supports NGN, GHS, ZAR, KES and more.</p>
        </div>
      </div>
      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2 block">Email Address</label>
        <input type="email" placeholder="your@email.com"
          value={email} onChange={e => setEmail(e.target.value)}
          className={`w-full px-4 py-4 bg-brand-light border-2 rounded-2xl outline-none transition-all text-sm ${emailError ? 'border-red-400' : 'border-brand-dark focus:border-accent-gold'}`}
        />
        {emailError && <p className="text-red-500 text-xs mt-1">{emailError}</p>}
      </div>
      <div className="bg-brand-light rounded-2xl p-4 border border-brand-dark">
        <div className="flex justify-between text-sm">
          <span className="text-text-muted font-light">Amount (USD)</span>
          <span className="font-bold text-accent-navy">${amount.toLocaleString()}</span>
        </div>
        <div className="flex justify-between text-sm mt-2">
          <span className="text-text-muted font-light">Charged in {paystackCurrency}</span>
          <span className="font-bold text-accent-gold">{paystackCurrency} {(amountInSubunit / 100).toLocaleString()}</span>
        </div>
      </div>
      <button onClick={handlePay} disabled={isProcessing}
        className="w-full py-5 bg-[#00C3F7] text-white rounded-2xl font-black uppercase tracking-widest text-[11px] flex items-center justify-center gap-3 hover:bg-[#00A8D6] transition-all shadow-xl active:scale-[0.98] disabled:opacity-60"
      >
        {isProcessing
          ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          : <><SafeIcon icon={FiZap} /> Pay with Paystack</>}
      </button>
    </div>
  );
};

// ─── Gateway Config Panel ──────────────────────────────────────────────────────
const GatewayConfigPanel = ({ onSave, onClose }) => {
  const [keys, setKeys] = useState(() => {
    try { return JSON.parse(localStorage.getItem('em_payment_keys') || '{}'); } catch { return {}; }
  });
  const [showKeys, setShowKeys] = useState({});

  const save = () => {
    localStorage.setItem('em_payment_keys', JSON.stringify(keys));
    onSave(keys);
    onClose();
  };

  const fields = [
    { id: 'stripePublishable', label: 'Stripe Publishable Key', hint: 'Starts with pk_test_ or pk_live_' },
    { id: 'paystackPublic',   label: 'Paystack Public Key',    hint: 'Starts with pk_test_ or pk_live_' },
  ];

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-accent-navy rounded-xl flex items-center justify-center">
          <SafeIcon icon={FiKey} className="text-accent-gold w-5 h-5" />
        </div>
        <div>
          <h4 className="font-serif text-lg text-accent-navy">Payment Gateway Keys</h4>
          <p className="text-[10px] text-text-muted font-light">Stored locally. Never sent to our servers.</p>
        </div>
      </div>
      {fields.map(field => (
        <div key={field.id}>
          <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2 block">{field.label}</label>
          <div className="relative">
            <input
              type={showKeys[field.id] ? 'text' : 'password'}
              placeholder={field.hint}
              value={keys[field.id] || ''}
              onChange={e => setKeys({ ...keys, [field.id]: e.target.value })}
              className="w-full pl-4 pr-12 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold transition-all text-xs font-mono"
            />
            <button type="button" onClick={() => setShowKeys(prev => ({ ...prev, [field.id]: !prev[field.id] }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted">
              <SafeIcon icon={showKeys[field.id] ? FiEyeOff : FiEye} className="w-4 h-4" />
            </button>
          </div>
          <p className="text-[10px] text-text-muted mt-1 font-light">{field.hint}</p>
        </div>
      ))}
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
        <SafeIcon icon={FiAlertCircle} className="text-amber-500 w-4 h-4 mt-0.5 shrink-0" />
        <p className="text-[10px] text-amber-700 leading-relaxed">
          For production, Stripe requires a server-side secret key. Paystack supports server-side verification via webhooks.
        </p>
      </div>
      <div className="flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 border-2 border-brand-dark rounded-2xl text-[10px] font-black uppercase tracking-widest text-text-muted hover:border-accent-gold transition-all">
          Cancel
        </button>
        <button onClick={save} className="flex-1 py-3 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all">
          Save Keys
        </button>
      </div>
    </div>
  );
};

// ─── Order Review Summary ──────────────────────────────────────────────────────
const OrderReviewSummary = ({ specialInstructions, giftOptions }) => {
  const hasGift  = giftOptions?.giftWrap;
  const hasInstr = specialInstructions?.trim();
  if (!hasGift && !hasInstr) return null;

  const wrapStyle = WRAP_STYLES.find(s => s.id === giftOptions?.wrapStyle);

  return (
    <div className="space-y-3 mb-6">
      {hasGift && (
        <div className="flex items-start gap-3 px-4 py-3 bg-accent-gold/10 border border-accent-gold/30 rounded-2xl">
          <span className="text-lg mt-0.5">{wrapStyle?.emoji || '🎁'}</span>
          <div className="flex-1 min-w-0">
            <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold mb-0.5">
              {wrapStyle?.label || 'Gift'} Wrap — Gift Order
            </p>
            <p className="text-[10px] text-accent-navy font-bold">To: {giftOptions.recipientName}</p>
            {giftOptions.giftMessage && (
              <p className="text-[10px] text-text-muted italic mt-1 leading-relaxed line-clamp-2">
                "{giftOptions.giftMessage}"
              </p>
            )}
          </div>
        </div>
      )}
      {hasInstr && (
        <div className="flex items-start gap-3 px-4 py-3 bg-brand-light border border-brand-dark rounded-2xl">
          <SafeIcon icon={FiClipboard} className="text-accent-gold w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold mb-1">Special Instructions</p>
            <p className="text-xs text-text-main italic leading-relaxed line-clamp-3">"{specialInstructions}"</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main PaymentGateway Modal ─────────────────────────────────────────────────
const PaymentGateway = ({
  isOpen, onClose, amount, currency,
  specialInstructions = '',
  giftOptions = { giftWrap: false, wrapStyle: 'classic', giftMessage: '', recipientName: '', address: '' },
  onPaymentSuccess,
}) => {
  const [gateway, setGateway] = useState('stripe');
  const [showConfig, setShowConfig] = useState(false);
  const [paymentState, setPaymentState] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [keys, setKeys] = useState(() => {
    try { return JSON.parse(localStorage.getItem('em_payment_keys') || '{}'); } catch { return {}; }
  });

  const handleSuccess = result => {
    setPaymentState('success');
    setTimeout(() => {
      onPaymentSuccess?.(result);
      onClose();
      setPaymentState('idle');
    }, 3000);
  };

  const handleError = msg => {
    setErrorMsg(msg);
    setPaymentState('error');
    setTimeout(() => setPaymentState('idle'), 4000);
  };

  if (!isOpen) return null;

  const wrapStyle = WRAP_STYLES.find(s => s.id === giftOptions?.wrapStyle);

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-md my-8 overflow-hidden"
      >
        {/* Header */}
        <div className="p-8 border-b border-brand-light bg-brand-light/30">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-serif text-accent-navy">
                {giftOptions?.giftWrap ? 'Gift Checkout' : 'Secure Checkout'}
              </h3>
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">
                Total: ${amount?.toLocaleString()}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {giftOptions?.giftWrap && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-accent-gold/10 border border-accent-gold/30 rounded-full">
                  <span className="text-sm">{wrapStyle?.emoji || '🎁'}</span>
                  <span className="text-[9px] font-black uppercase tracking-widest text-accent-gold">Gift</span>
                </div>
              )}
              <button
                onClick={() => setShowConfig(!showConfig)}
                className={`p-2 rounded-xl transition-all border ${showConfig ? 'bg-accent-gold text-white border-accent-gold' : 'bg-brand-light text-text-muted border-brand-dark hover:border-accent-gold'}`}
                title="Configure API Keys"
              >
                <SafeIcon icon={FiKey} className="w-4 h-4" />
              </button>
              <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full transition-colors">
                <SafeIcon icon={FiX} className="w-6 h-6 text-text-muted" />
              </button>
            </div>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {showConfig ? (
            <motion.div key="config" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <GatewayConfigPanel onSave={setKeys} onClose={() => setShowConfig(false)} />
            </motion.div>
          ) : paymentState === 'success' ? (
            <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              className="p-12 text-center"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <SafeIcon icon={FiCheck} className="w-10 h-10 text-green-600" />
              </div>
              <h4 className="text-2xl font-serif text-accent-navy mb-3">
                {giftOptions?.giftWrap ? 'Gift Order Placed! 🎁' : 'Payment Successful!'}
              </h4>
              <p className="text-text-muted text-sm font-light mb-6">
                {giftOptions?.giftWrap
                  ? `Your gift for ${giftOptions.recipientName} will be beautifully wrapped and dispatched.`
                  : 'Your order has been placed. A confirmation will be sent to your email.'}
              </p>
              {giftOptions?.giftWrap && giftOptions.giftMessage && (
                <div className="text-left bg-accent-gold/10 border border-accent-gold/30 rounded-2xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiHeart} className="text-rose-400 w-3.5 h-3.5" />
                    <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold">Gift Message</p>
                  </div>
                  <p className="text-xs text-text-main italic">"{giftOptions.giftMessage}"</p>
                </div>
              )}
              {specialInstructions?.trim() && (
                <div className="text-left bg-brand-light border border-brand-dark rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <SafeIcon icon={FiClipboard} className="text-accent-gold w-3.5 h-3.5" />
                    <p className="text-[9px] font-black uppercase tracking-widest text-accent-gold">Instructions Noted</p>
                  </div>
                  <p className="text-xs text-text-main italic">"{specialInstructions}"</p>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8">
              {/* Gateway Selector */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { id: 'stripe',   label: 'Credit / Debit Card', sub: 'Powered by Stripe', emoji: '💳' },
                  { id: 'paystack', label: 'Paystack',            sub: 'Africa & Global',   emoji: '⚡' },
                ].map(g => (
                  <button key={g.id} onClick={() => setGateway(g.id)}
                    className={`p-4 rounded-2xl border-2 transition-all text-left ${gateway === g.id ? 'border-accent-gold bg-brand-light' : 'border-brand-dark hover:border-accent-gold/40'}`}
                  >
                    <div className="text-2xl mb-2">{g.emoji}</div>
                    <p className="text-xs font-black text-accent-navy">{g.label}</p>
                    <p className="text-[9px] text-text-muted font-light">{g.sub}</p>
                  </button>
                ))}
              </div>

              {/* Order Review — gift + instructions */}
              <OrderReviewSummary specialInstructions={specialInstructions} giftOptions={giftOptions} />

              {/* Error Banner */}
              <AnimatePresence>
                {paymentState === 'error' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                    className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3">
                    <SafeIcon icon={FiAlertCircle} className="text-red-500 w-4 h-4 mt-0.5 shrink-0" />
                    <p className="text-xs text-red-700">{errorMsg}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Missing Key Warnings */}
              {gateway === 'stripe' && !keys.stripePublishable && (
                <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-3 flex items-center gap-3">
                  <SafeIcon icon={FiKey} className="text-amber-500 w-4 h-4 shrink-0" />
                  <p className="text-[10px] text-amber-700">
                    Stripe key not configured.{' '}
                    <button onClick={() => setShowConfig(true)} className="font-black underline">Add it here</button>
                  </p>
                </div>
              )}
              {gateway === 'paystack' && !keys.paystackPublic && (
                <div className="mb-6 bg-amber-50 border border-amber-200 rounded-2xl p-3 flex items-center gap-3">
                  <SafeIcon icon={FiKey} className="text-amber-500 w-4 h-4 shrink-0" />
                  <p className="text-[10px] text-amber-700">
                    Paystack key not configured.{' '}
                    <button onClick={() => setShowConfig(true)} className="font-black underline">Add it here</button>
                  </p>
                </div>
              )}

              {gateway === 'stripe'   && <StripeForm   amount={amount} onSuccess={handleSuccess} onError={handleError} publishableKey={keys.stripePublishable} />}
              {gateway === 'paystack' && <PaystackForm amount={amount} onSuccess={handleSuccess} onError={handleError} publicKey={keys.paystackPublic} currency={currency} />}

              <div className="mt-6 flex items-center justify-center gap-2 text-[9px] font-black uppercase tracking-widest text-text-muted opacity-50">
                <SafeIcon icon={FiLock} /> SSL Encrypted · PCI Compliant
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default PaymentGateway;